import { ApolloClient, ApolloLink, from, HttpLink, InMemoryCache } from "@apollo/client";
import { onError } from "@apollo/client/link/error";
import { GraphQLFormattedError } from "graphql/error";
import { LocalStorage } from "../../types/enums";
import { RetryLink } from "@apollo/client/link/retry";
import { smartTriggerNotification } from "@/utils/notificationDebouncer";
import { 
    checkCurrentFrontendAuthState, 
    determineAuthErrorAction, 
    isOnLoginPage 
} from "@/utils/authStateDetection";
import { determineAuthErrorMessage } from "@/utils/authMessages";
import { 
    createLogoutSuccessTracker, 
    isLogoutInProgress 
} from "@/utils/logoutTracker";

// Authentication handler for logout - set by App.tsx
let authHandler: ((skipNotification?: boolean) => void) | null = null;
let dispatch: any = null; // Redux dispatch for notifications

// Extend window type for auth error tracking
declare global {
    interface Window {
        lastAuthError?: { [key: string]: number };
    }
}

export const setAuthHandler = (handler: ((skipNotification?: boolean) => void) | null) => { 
    authHandler = handler; 
};

export const setDispatch = (reduxDispatch: any) => {
    dispatch = reduxDispatch;
};

const errorLink = onError(({ graphQLErrors, networkError, operation }) => {
    if (graphQLErrors) {
        console.log("🔍 Apollo GraphQL Errors detected:", graphQLErrors.length);
        
        graphQLErrors.forEach(({ path, message, extensions }: GraphQLFormattedError) => {
            console.error("GraphQL Error:", { path, message, extensions, operation: operation.operationName });

            // Check for authentication failures
            const isAuthError = checkIfAuthenticationError(message, extensions);

            if (isAuthError) {
                handleAuthenticationError(operation.operationName, 'graphql', { message, extensions });
                return;
            }
        });
    }

    if (networkError) {
        console.error(`[Network Error]`, networkError);
        
        // Check for authentication-related network errors (401, 403)
        const statusCode = (networkError as any)?.statusCode || (networkError as any)?.response?.status;
        if (statusCode === 401 || statusCode === 403) {
            console.warn("🚨 Network authentication error detected", { statusCode });
            handleAuthenticationError(undefined, 'network', { statusCode, error: networkError });
            return;
        }
    }
});

/**
 * Check if an error indicates authentication failure
 */
const checkIfAuthenticationError = (message: string, extensions?: any): boolean => {
    // Check message for auth indicators
    const isAuthMessage = 
        message === "Context creation failed: jwt expired" ||
        message === "Context creation failed: invalid signature" ||
        message === "Context creation failed: invalid token" ||
        message.includes("UNAUTHENTICATED") ||
        message.includes("access denied") ||
        message.toLowerCase().includes("unauthenticated") ||
        message.toLowerCase().includes("unauthorized");

    // Check extensions for auth errors
    const isAuthExtension = extensions && (
        (extensions as any).code === "UNAUTHENTICATED" ||
        (extensions as any).code === "UNAUTHORIZED" ||
        ((extensions as any).exception && (extensions as any).exception.message && 
         (extensions as any).exception.message.includes("UNAUTHENTICATED"))
    );

    return isAuthMessage || isAuthExtension;
};

/**
 * Handle authentication errors with smart context-aware logic
 */
const handleAuthenticationError = (
    operationName?: string, 
    errorType: 'graphql' | 'network' = 'graphql',
    errorDetails?: any
) => {
    console.warn("🚨 Authentication error detected", { 
        operationName, 
        errorType, 
        errorDetails 
    });

    // Extra safety: Rate limit auth error handling to prevent loops
    const now = Date.now();
    const key = `auth_error_${operationName || 'unknown'}`;
    if (!window.lastAuthError) window.lastAuthError = {};
    if (window.lastAuthError[key] && (now - window.lastAuthError[key]) < 1000) {
        console.log("⏭️ Auth error handling rate-limited to prevent loops");
        return;
    }
    window.lastAuthError[key] = now;

    // Check current frontend auth state first
    const authState = checkCurrentFrontendAuthState();
    const action = determineAuthErrorAction(authState, operationName);
    
    // If on login page, only show informational messages, no actions
    if (isOnLoginPage()) {
        console.log("⏭️ On login page - only showing informational message, no actions");
        
        // Show appropriate message but don't perform any logout/redirect actions
        if (dispatch && authState.tokenExists) {
            // Only show notification if user had a token (session expiration scenario)
            const message = determineAuthErrorMessage(authState, 'informational_redirect', operationName);
            smartTriggerNotification(message, dispatch);
        }
        
        // Always return early when on login page to prevent any actions
        return;
    }

    // If on a public page (invite, onboarding, etc.), let the page handle the error gracefully
    // Don't redirect away from public pages - they should manage their own auth flow
    if (authState.isOnPublicPage) {
        console.log("⏭️ On public page - letting component handle GraphQL errors gracefully, no redirect");
        
        // Only show minimal notification if user had a valid token that expired
        // (e.g., user was logged in, opened invite link in new tab, token expired)
        if (dispatch && authState.tokenExists && authState.authStateSource === 'token') {
            const message = {
                title: "Session Expired",
                content: "Your session has expired, but you can continue with this page.",
                type: "warning" as const,
                duration: 4000
            };
            smartTriggerNotification(message, dispatch);
        }
        
        // Always return early for public pages - no logout/redirect actions
        return;
    }


    // Skip if logout already in progress
    if (isLogoutInProgress()) {
        console.log("⏭️ Logout already in progress, skipping duplicate auth error");
        return;
    }
    
    console.log("🎯 Auth error action determined", { authState, action });

    // Create logout tracker for monitoring success
    const logoutTracker = createLogoutSuccessTracker(`auth_error_${errorType}`);

    switch (action) {
        case 'logout':
            // User was authenticated, perform full logout with notification
            logoutTracker.markLogoutStarted();
            
            if (dispatch) {
                // Use smart notification to prevent spam
                const message = determineAuthErrorMessage(authState, action, operationName);
                const notificationSent = smartTriggerNotification(message, dispatch);
                
                if (notificationSent && authHandler) {
                    // Skip the default logout notification since we're showing our own
                    authHandler(true); // true = skip default notification
                } else if (authHandler) {
                    authHandler(false); // false = show default notification if our custom one failed
                }
            } else if (authHandler) {
                authHandler(false);
            } else {
                fallbackDirectRedirect('auth_error_logout');
            }
            break;

        case 'informational_redirect':
            // Show informational message and redirect
            if (dispatch) {
                const message = determineAuthErrorMessage(authState, action, operationName);
                smartTriggerNotification(message, dispatch);
            }
            setTimeout(() => {
                redirectToLogin('auth_error_info');
            }, 1500); // Small delay to let user read the message
            break;

        case 'silent_redirect':
            // Just redirect without notification
            redirectToLogin('auth_error_silent');
            break;

        default:
            console.warn("Unknown auth error action, falling back to direct redirect");
            fallbackDirectRedirect('auth_error_unknown');
            break;
    }
};

/**
 * Fallback direct redirect when handlers are not available
 */
const fallbackDirectRedirect = (reason: string) => {
    console.warn("🚨 Using fallback direct redirect", { reason });
    localStorage.clear();
    if (typeof window !== 'undefined' && window.location) {
        const currentPath = window.location.pathname;
        const returnUrl = currentPath !== '/login' ? `?returnTo=${encodeURIComponent(currentPath)}` : '';
        window.location.assign(`/login?reason=${reason}${returnUrl}`);
    }
};

/**
 * Simple redirect to login
 */
const redirectToLogin = (reason: string) => {
    if (typeof window !== 'undefined' && window.location) {
        const currentPath = window.location.pathname;
        const returnUrl = currentPath !== '/login' ? `?returnTo=${encodeURIComponent(currentPath)}` : '';
        window.location.assign(`/login?reason=${reason}${returnUrl}`);
    }
};

const authLink = new ApolloLink((operation, forward) => {
    const token = localStorage.getItem(LocalStorage.AUTH_TOKEN);

    operation.setContext({
        headers: { Authorization: token ? `Bearer ${token}` : "", "Content-Type": "application/json" },
    });

    return forward(operation);
});

const httpLink = new HttpLink({ uri: process.env.REACT_APP_BACKEND_DOMAIN });

// Retry transient transport errors (429, 5xx) up to 4 times with exponential back-off
const retryLink = new RetryLink({
    delay: { initial: 500, max: 30000, jitter: true },
    attempts: {
        max: 10,
        retryIf: (error) => {
            if (!error) return false;
            // Apollo passes the network error as "error" with a statusCode field
            // (for HttpLink). Fallback to response?.status for safety.
            const status: number | undefined = (error as any).statusCode ?? (error as any).response?.status;
            return status === 429 || (status !== undefined && status >= 500);
        },
    },
});

const link = from([errorLink, retryLink, authLink, httpLink]);
const cache = new InMemoryCache({
    typePolicies: {
        Chat: {
            keyFields: ["id"],
        },
        Query: {
            fields: {
                chat: {
                    keyArgs: ["id"],
                },
            },
        },
    },
});

const client = new ApolloClient({
    cache,
    link,
    devtools: {
        enabled: true,
    },
    defaultOptions: {
        watchQuery: {
            fetchPolicy: "cache-and-network",
            errorPolicy: "all", // Allow errors to reach error link for auth handling
        },
    },
});

export default client;
